import { Check, Clock, ShieldCheck, BarChart3, Zap, TrendingUp, Smile } from "lucide-react";
import Reveal from "../Reveal";

const blocks = [
  {
    icon: Smile,
    eyebrow: "Easy to use",
    title: "Anyone on your team can master it in a single shift.",
    desc: "A clean interface, large touch targets, and keyboard shortcuts make FolioStax effortless for cashiers, owners and accountants alike.",
    bullets: ["Clean, simple interface", "Large, tap-friendly buttons", "Handy keyboard shortcuts"],
  },
  {
    icon: Clock,
    eyebrow: "Saves time",
    title: "Cut billing time by up to 60% — every single day.",
    desc: "Bulk scan, hotkeys, saved customers and one-click invoicing let your counter move 2x faster during peak hours.",
    bullets: ["Fast, few-tap billing", "One-click invoice + share", "Auto-fill saved customer details"],
    flip: true,
  },
  {
    icon: ShieldCheck,
    eyebrow: "Reduces errors",
    title: "Goodbye manual mistakes. Hello rock-solid accuracy.",
    desc: "Totals, discounts and stock are all calculated automatically — no more end-of-day rework.",
    bullets: ["Real-time inventory sync", "Audit log on every change", "Automatic totals & discounts"],
  },
  {
    icon: BarChart3,
    eyebrow: "Real-time reports",
    title: "Make sharper decisions with live business intelligence.",
    desc: "See revenue, top items, low stock and outstanding dues in real time — from your shop, your home or your phone.",
    bullets: ["Daily, weekly, monthly views", "Profit & loss at a glance", "Mobile dashboard included"],
    flip: true,
  },
];

export default function Benefits() {
  return (
    <section id="benefits" data-testid="benefits-section" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800">Why teams choose us</p></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mt-3">
              Built for the way modern retail actually works.
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 space-y-20 lg:space-y-28">
          {blocks.map((b, i) => (
            <div key={b.eyebrow} className={`grid lg:grid-cols-2 gap-12 items-center ${b.flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <Reveal>
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]">
                    <b.icon className="h-3.5 w-3.5" /> {b.eyebrow}
                  </div>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900 mt-4">
                    {b.title}
                  </h3>
                  <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{b.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {b.bullets.map((bl) => (
                      <li key={bl} className="flex items-start gap-3 text-sm text-slate-700">
                        <span className="h-5 w-5 shrink-0 rounded-full bg-blue-800 grid place-items-center mt-0.5">
                          <Check className="h-3 w-3 text-white" />
                        </span>
                        <span>{bl}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={120}>
                <BenefitVisual index={i} />
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitVisual({ index }) {
  if (index === 0) {
    return (
      <div className="rounded-3xl bg-slate-50 border border-slate-200 p-8 lg:p-10">
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-5">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-3">
            <p className="font-display font-bold text-slate-900">New Bill</p>
            <span className="text-[11px] font-mono text-slate-500">Bill #2049</span>
          </div>
          <div className="space-y-2">
            {[
              { n: "Cappuccino", q: 2, p: 220 },
              { n: "Croissant", q: 1, p: 140 },
              { n: "Sandwich", q: 1, p: 220 },
            ].map((r) => (
              <div key={r.n} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{r.n} × {r.q}</span>
                <span className="font-display font-semibold text-slate-900">₹{r.p * r.q}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-dashed border-slate-200 mt-4 pt-3 flex justify-between items-center">
            <span className="text-sm text-slate-500">Total</span>
            <span className="font-display font-black text-2xl text-slate-900">₹580</span>
          </div>
          <button className="mt-4 w-full rounded-full bg-blue-800 hover:bg-blue-700 text-white text-sm font-semibold py-3 transition">Print &amp; Save</button>
        </div>
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="rounded-3xl bg-navy text-white p-8 lg:p-10 grain relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-blue-800/20 blur-3xl" />
        <p className="text-[11px] uppercase tracking-[0.2em] text-blue-700 font-bold">Built for speed</p>
        <p className="font-display text-5xl font-black mt-3">Seconds<span className="text-2xl text-slate-400"> / bill</span></p>
        <p className="mt-2 text-slate-300 text-sm">Scan, confirm, print — billing built for peak-hour rushes.</p>
        <div className="mt-6 grid grid-cols-3 gap-3">
          {["Scan", "Confirm", "Print"].map((s, i) => (
            <div key={s} className="rounded-xl bg-white/5 border border-white/10 p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Step {i + 1}</p>
              <p className="font-display font-bold mt-1">{s}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="rounded-3xl bg-slate-50 border border-slate-200 p-8 lg:p-10">
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-5 space-y-3">
          <p className="font-display font-bold text-slate-900">Inventory check</p>
          {[
            { n: "Atta 5kg", s: 142, t: "OK" },
            { n: "Sugar 1kg", s: 6, t: "Low" },
            { n: "Milk 1L", s: 0, t: "Out" },
          ].map((it) => (
            <div key={it.n} className="flex items-center justify-between text-sm">
              <span className="text-slate-700">{it.n}</span>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                it.t === "OK" ? "bg-blue-50 text-blue-700" :
                it.t === "Low" ? "bg-yellow-50 text-yellow-700" :
                "bg-red-50 text-red-700"
              }`}>{it.t} • {it.s}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-3xl bg-navy text-white p-8 lg:p-10 grain relative overflow-hidden">
      <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-blue-700/20 blur-3xl" />
      <p className="text-[11px] uppercase tracking-[0.2em] text-blue-700 font-bold">Today vs Yesterday</p>
      <div className="mt-3 flex items-end gap-4">
        <p className="font-display text-5xl font-black">+18%</p>
        <span className="inline-flex items-center text-blue-700 text-sm font-semibold pb-2">
          <TrendingUp className="h-4 w-4 mr-1" /> growth
        </span>
      </div>
      <div className="mt-6 flex items-end gap-2 h-24">
        {[40, 55, 38, 70, 52, 88, 76].map((h, i) => (
          <div key={i} style={{ height: `${h}%` }} className={`flex-1 rounded-t ${i === 5 ? "bg-blue-700" : "bg-white/15"}`} />
        ))}
      </div>
    </div>
  );
}
